import { useDispatch, useSelector } from "react-redux";
import useAlerts from "./useAlerts";
import useLoading from "./useLoading";

import {
  setLoading,
  setSubmitting,
  setSupplierList,
  setSuppliersDropdown,
  setSupplierDetail,
  updateSupplierStatusInList,
  updateField,
  resetSupplierForm,
  setPaginator,
  setShowRegistrationModal,
  setShowViewModal,
  setShowEditModal,
  setErrors,
  addSupplierToList,
  setSupplierFormForEdit,
  clearSupplierList,
} from "../store/slices/supplier/supplierSlice";

import { RootState, AppDispatch } from "../store/store";
import { ISupplierForm } from "../types/supplier/supplierInterfaces";
import useApiErrorHandler from "./useApiErrorHandler";
import SupplierService from "../services/supplier/SupplierService";

export const useSupplier = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const {
    form,
    detail,
    list,
    paginator,
    isLoading,
    isSubmitting,
    suppliersDropdown,
    showRegistrationModal,
    showViewModal,
    showEditModal,
    errors,
  } = useSelector((state: RootState) => state.suppliers);

  /**
   * 🔍 Obtener proveedores paginados
   */
  const fetchSuppliersPaged = async (page = 0, size = 10) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();
      const response = await SupplierService.getAllPaged(page, size);
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(setSupplierList(data.content));
        dispatch(setPaginator(data));
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
   * 🔎 Obtener proveedores filtrados
   */
  const fetchFilteredSuppliers = async (
    filters: any,
    page = 0,
    size = 10,
    sort = "name,asc"
  ) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();
      const response = await SupplierService.getFiltered(filters, page, size, sort);
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(setSupplierList(data.content));
        dispatch(setPaginator(data));
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
 * 💾 Guardar o actualizar proveedor
 */
  const saveSupplier = async () => {
    const payload = form;
    const isUpdate = payload.id !== null && payload.id !== undefined && payload.id > 0;

    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const response = isUpdate
        ? await SupplierService.modify(payload.id!, payload)
        : await SupplierService.add(payload);

      const { statusCode, type, title, message, data: supplierResponse } = response;

      /** ✅ Éxito tanto en actualización (200) como creación (201) **/
      const success = statusCode === 200 || statusCode === 201;

      if (success) {

        // ✅ Solo agregamos a la lista paginada si es creación
        if (!isUpdate && supplierResponse) {
          dispatch(addSupplierToList(supplierResponse));
        }

        // ✅ Alerta de éxito
        showAlert(type, title, message);

        // ✅ Cierra modal según el flujo
        if (isUpdate) {
          closeEditModal();
        } else {
          closeAddModal();
        }

        // ✅ Limpia errores anteriores
        dispatch(setErrors(null));

        // ✅ Refresca tabla
        fetchSuppliersPaged();
        return;
      }

      // ❌ Caso extraño (ni 200 ni 201)
      showAlert(
        "warning",
        "Operación no completada",
        "El servidor no confirmó la operación."
      );

    } catch (err: any) {

      /** ❌ errores con fieldErrors desde backend **/
      if (err.response?.data?.data) {
        dispatch(setErrors(err.response.data.data));
      }

      handle(err);

    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };


  /**
   * ✏️ Cargar proveedor por ID para edición
   */
  const loadSupplierForEdit = async (id: number) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      const response = await SupplierService.getSupplierForEdit(id);
      const { statusCode, data } = response;

      if (statusCode === 200) {
        dispatch(setSupplierFormForEdit(data));
        openEditModal();
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
   * 👁️ Obtener proveedor por ID (vista detalle)
   */
  const loadSupplierById = async (id: number) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      const response = await SupplierService.getById(id);
      const { statusCode, data } = response;

      if (statusCode === 200) {
        dispatch(setSupplierDetail(data));
        openViewModal();
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
   * 🔄 Alternar estado activo/inactivo
   */
  const toggleStatus = async (id: number) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const response = await SupplierService.toggleStatus(id);
      if (response.data?.statusCode === 200) {
        dispatch(updateSupplierStatusInList({ id, status: response.data.data.status }));
        showAlert(response.data.type, response.data.title, response.data.message);
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  /**
   * 📥 Obtener proveedores para dropdown
   */
  const handlerGetSuppliersForDropdown = async () => {
    try {
      const response = await SupplierService.getDropdown();
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(setSuppliersDropdown(data));
      }
    } catch (error) {
      handle(error);
    }
  };

  const updateFormField = <K extends keyof ISupplierForm>(field: K, value: any) => {
    dispatch(updateField({ field, value }));
  };

  const resetForm = () => {
    dispatch(resetSupplierForm());
  };

  // 👇 Control de modales
  const openAddModal = () => dispatch(setShowRegistrationModal(true));
  const closeAddModal = () => dispatch(setShowRegistrationModal(false));

  const openViewModal = () => dispatch(setShowViewModal(true));
  const closeViewModal = () => dispatch(setShowViewModal(false));

  const openEditModal = () => dispatch(setShowEditModal(true));
  const closeEditModal = () => dispatch(setShowEditModal(false));

  const changeEntityType = (newType: number) => {
    dispatch(resetSupplierForm());
    dispatch(updateField({ field: "entityTypeId", value: newType }));
  };

  const handlerClearFilteredSuppliers = () => {
    dispatch(clearSupplierList());
  };

  return {
    // 📌 Estados
    form,
    detail,
    list,
    paginator,
    isLoading,
    isSubmitting,
    suppliersDropdown,
    showRegistrationModal,
    showViewModal,
    showEditModal,
    errors,

    // 📌 Métodos
    fetchSuppliersPaged,
    fetchFilteredSuppliers,
    handlerGetSuppliersForDropdown,
    saveSupplier,
    loadSupplierForEdit,
    loadSupplierById,
    toggleStatus,

    updateFormField,
    resetForm,

    openAddModal,
    closeAddModal,
    openViewModal,
    closeViewModal,
    openEditModal,
    closeEditModal,

    changeEntityType,

    handlerClearFilteredSuppliers,
  };
};
